/**
 * @module keepInPlace
 *
 * B102 (Keep a plant where it was drawn): when the gardener changes a plant on the Plants page
 * (its stage, a photo, an edit: PlantsContext.updateSinglePlant fires plantChanged) the server's order ("Needs you first", "Stage", ...) may move
 * it somewhere far off, and she loses it. This keeps such a plant in the place it was drawn in
 * until she leaves the page or changes the sort or view. It is UI state only: the server's order
 * is not changed, and nothing here decides anything about a plant.
 *
 * The same idea as `justAdded` in PlantGrid, which pins a new plant to the front.
 */
import { useEffect, useMemo, useRef } from 'react';
import type { Plant } from '@/shared/types/plant';
import { takeChangedPlants } from '@/shared/lib/plantChanged';
import { keepInPlace } from './keepInPlaceOrder';

/**
 * Keeps a plant the gardener just changed (shared/lib/plantChanged; signature changes alone pin nothing) in the place it was drawn in, in cards, grid and list.
 * Released when `viewKey` changes (sort, filter or view) or the page unmounts. If a held plant
 * ends up off screen, scrolls to its `plant-card-<id>` anchor.
 *
 * @param arranged - The plants in the server's order for the current sort.
 * @param viewKey - Changes whenever the sort or view changes.
 * @returns The plants, with changed ones held in their drawn place.
 */
export function useKeepInPlace(arranged: Plant[], viewKey: string): Plant[] {
  const state = useRef({ key: viewKey, drawn: [] as number[], pinned: new Set<number>(), fresh: [] as number[] });
  const shown = useMemo(() => {
    const s = state.current;
    if (s.key !== viewKey) {
      s.key = viewKey;
      s.pinned = new Set();
    }
    // Only the gardener's own saves pin; a refetch pins nothing.
    for (const id of takeChangedPlants()) {
      if (s.drawn.includes(id) && !s.pinned.has(id)) {
        s.pinned.add(id);
        s.fresh.push(id);
      }
    }
    const ids = keepInPlace(arranged.map((p) => p.plant_obj_id), s.drawn, s.pinned);
    s.drawn = ids;
    const byId = new Map(arranged.map((p) => [p.plant_obj_id, p]));
    return ids.map((id) => byId.get(id) as Plant);
  }, [arranged, viewKey]);

  useEffect(() => {
    const s = state.current;
    const id = s.fresh.pop();
    s.fresh = [];
    if (id === undefined) return;
    const el = document.getElementById(`plant-card-${id}`);
    if (!el) return;
    const box = el.getBoundingClientRect();
    if (box.bottom < 0 || box.top > window.innerHeight) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [shown]);

  return shown;
}
